// src/hooks/useProctorRecorder.js
// Segmentowe nagrywanie WEBM (VP8/Opus) → presigned PUT do S3.
// Indeks per chunk pobieramy z backendu (/next), presign (/presign), po egzaminie /finalize.
// UWAGA: Nie wysyłamy żadnego "ack" do bazy (nie trzymacie segmentów w DB).

import { useCallback, useEffect, useRef } from 'react';

const authHeader = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` });

const WEBM_MIME = 'video/webm;codecs=vp8,opus';
const WEBM_CT   = 'video/webm';

async function getUserMediaStream() {
    const savedId = localStorage.getItem('exam.cameraId') || '';
    const base = { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 24 } };
    let constraints = savedId
        ? { video: { ...base, deviceId: { exact: savedId } }, audio: true }
        : { video: { ...base, facingMode: { ideal: 'user' } }, audio: true };

    try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        try {
            const vTrack = stream.getVideoTracks()[0];
            const usedId = vTrack?.getSettings?.().deviceId;
            if (usedId) localStorage.setItem('exam.cameraId', usedId);
        } catch {}
        return stream;
    } catch (e) {
        if (savedId) {
            constraints = { video: { ...base, facingMode: { ideal: 'user' } }, audio: true };
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            return stream;
        }
        throw e;
    }
}

async function recordOneChunk({ stream, chunkSeconds, attemptId, idx }) {
    return new Promise((resolve, reject) => {
        let rec;
        try {
            rec = new MediaRecorder(stream, {
                mimeType: WEBM_MIME,
                videoBitsPerSecond: 1_200_000,
                audioBitsPerSecond: 64_000,
            });
        } catch (e) {
            return reject(new Error(`MediaRecorder init failed: ${e?.message || e}`));
        }

        const parts = [];
        let stopTimer = null;

        rec.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) parts.push(ev.data); };
        rec.onerror = (ev) => { try { rec.stop(); } catch {}; if (stopTimer) clearTimeout(stopTimer); reject(ev?.error || new Error('MediaRecorder error')); };

        rec.onstop = async () => {
            if (stopTimer) clearTimeout(stopTimer);
            try {
                const blob = new Blob(parts, { type: WEBM_MIME });
                if (!blob || blob.size === 0) throw new Error('Pusty fragment (0 B)');

                // presign
                const pres = await fetch(`/api/rec/attempts/${attemptId}/presign?idx=${idx}&mime=${encodeURIComponent(WEBM_CT)}`, {
                    credentials: 'include',
                    headers: { ...authHeader() }
                }).then(r => r.json());

                if (!pres?.url || !pres?.key) throw new Error('Presign nie zwrócił URL/Key');

                // upload do S3 (bez Authorization)
                const put = await fetch(pres.url, {
                    method: pres.method || 'PUT',
                    headers: { 'Content-Type': WEBM_CT, ...(pres.headers || {}) },
                    body: blob,
                });
                if (!put.ok){
                    const t = await put.text().catch(()=> '');
                    throw new Error(`Upload nieudany: ${put.status} ${t}`);
                }

                // brak ACK — nie trzymamy segmentów w DB
                resolve();
            } catch (err) {
                reject(err);
            }
        };

        try { rec.start(); } catch (e) { return reject(new Error(`MediaRecorder start failed: ${e?.message || e}`)); }
        stopTimer = setTimeout(() => { try { rec.stop(); } catch {} }, Math.max(1, chunkSeconds) * 1000);
    });
}

export function useProctorRecorder({
                                       attemptId,
                                       chunkSeconds = 30,
                                       onError = () => {},
                                   } = {}) {

    const runningRef = useRef(false);
    const loopRef = useRef(null);
    const streamRef = useRef(null);
    const stoppingRef = useRef(false);

    const fetchNextIdx = useCallback(async () => {
        try {
            const r = await fetch(`/api/rec/attempts/${attemptId}/next?t=${Date.now()}`, {
                credentials: 'include',
                headers: { ...authHeader() },
                cache: 'no-store',
            });
            const j = await r.json();
            const n = Number(j?.nextIdx);
            return Number.isFinite(n) && n >= 0 ? n : 0;
        } catch {
            return 0;
        }
    }, [attemptId]);

    const start = useCallback(async () => {
        if (runningRef.current) return;
        stoppingRef.current = false;

        if (!window.MediaRecorder || typeof window.MediaRecorder.isTypeSupported !== 'function' || !window.MediaRecorder.isTypeSupported(WEBM_MIME)) {
            onError(new Error('Ta przeglądarka nie wspiera nagrywania WEBM (VP8/Opus).'));
            return;
        }

        try {
            const stream = await getUserMediaStream();
            streamRef.current = stream;
        } catch (e) {
            onError(new Error(`Brak dostępu do kamery/mikrofonu: ${e?.message || e}`));
            return;
        }

        runningRef.current = true;

        loopRef.current = (async () => {
            while (runningRef.current && !stoppingRef.current) {
                try {
                    const idx = await fetchNextIdx();
                    await recordOneChunk({ stream: streamRef.current, chunkSeconds, attemptId, idx });
                } catch (e) {
                    onError(e);
                    await new Promise(r => setTimeout(r, 1000));
                }
            }
        })();
    }, [attemptId, chunkSeconds, fetchNextIdx, onError]);

    const stop = useCallback(async () => {
        if (!runningRef.current) return;
        stoppingRef.current = true;
        runningRef.current = false;
        try { await loopRef.current; } catch {}
        loopRef.current = null;
        try {
            const s = streamRef.current;
            if (s) s.getTracks().forEach(t => { try { t.stop(); } catch {} });
        } finally {
            streamRef.current = null;
        }
    }, []);

    const finalize = useCallback(async () => {
        try {
            await fetch(`/api/rec/attempts/${attemptId}/finalize`, {
                method: 'POST',
                credentials: 'include',
                headers: { ...authHeader() },
            });
        } catch {}
    }, [attemptId]);

    useEffect(() => {
        return () => {
            runningRef.current = false;
            stoppingRef.current = true;
            try {
                const s = streamRef.current;
                if (s) s.getTracks().forEach(t => { try { t.stop(); } catch {} });
            } catch {}
            streamRef.current = null;
        };
    }, []);

    const isRunning = useCallback(() => runningRef.current, []);

    return { start, stop, finalize, isRunning };
}
